<template>
  <section id="quienessomos" class="about-section">
    <vue-headful :title="title" />
    
    <div class="bg-layer">
      <div class="net-grid"></div>
      <div v-for="i in 8" :key="i" class="node" :style="nodeStyle(i)"></div>
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
    </div>

    <v-container class="content relative">
      <v-row justify="center" class="py-12 text-center">
        <v-col cols="12" md="10" lg="8" class="overflow-hidden">
          <div class="binary-scroll-container mb-4">
            <div class="binary-scrolling-text code-font primary--text opacity-50">
              00111010 00101111 01001110 01001001 01000011 00111010 00101111 01001110 01001001 01000011
            </div>
          </div>
          
          <div class="mb-8">
            <div class="code-font primary--text text-h6 tracking-widest mb-2">nic.ni</div>
            <h1 class="text-h3 text-md-h1 font-weight-black lh-1">
              <span class="d-block white--text">QUÉ ES EL</span>
              <span class="d-block nic-gradient mt-n2">.NI</span>
            </h1>
            <div class="scan-line my-6 mx-auto"></div>
          </div>
          <p class="code-font primary--text text-h6">El corazón digital de Nicaragua desde 2013</p>
        </v-col>
      </v-row>

      <div class="py-12 relative">
        <div class="timeline-line"></div>
        <div v-for="(h, i) in historias" :key="i" 
             class="timeline-item d-flex align-center mb-12" 
             :class="i % 2 === 0 ? 'flex-row' : 'flex-row-reverse text-right'"
             @mouseenter="activeT = i" @mouseleave="activeT = null">
          
          <div class="timeline-marker">
            <div class="marker-dot" :class="{active: activeT===i}"></div>
            <div class="caption grey--text mt-2">{{ ['2013', '2014', '2015', 'Presente'][i] }}</div>
          </div>
          
          <div class="timeline-content" :class="{active: activeT===i}">
            <div class="glass-card pa-6">
              <h3 class="text-h6 font-weight-bold white--text mb-2">{{ titles[i] }}</h3>
              <div class="d-flex flex-wrap gap-2 mb-4" :class="i % 2 !== 0 ? 'justify-end' : ''">
                <span v-for="t in tags[i]" :key="t" class="chip">{{t}}</span>
              </div>
              <p class="grey--text text--lighten-1 mb-0">{{ h.text }}</p>
            </div>
          </div>
        </div>
      </div>

      <v-row class="my-12">
        <v-col cols="12" md="6" v-for="(item, k) in mv" :key="k">
          <div class="glass-card hover-lift pa-8 h-100 relative overflow-hidden group">
            <v-icon x-large class="mb-6 pa-4 rounded-circle glass-bg transition-all">{{ item.icon }}</v-icon>
            <h3 class="text-h4 font-weight-bold white--text mb-4">{{ item.title }}</h3>
            <p class="grey--text text--lighten-1">{{ item.text }}</p>
            <div class="glow-bar"></div>
          </div>
        </v-col>
      </v-row>

      <div class="py-12">
        <h2 class="section-title text-center mb-12">NUESTRO IMPACTO</h2>
        <div class="grid-auto-fit gap-6">
          <div v-for="(s, i) in stats" :key="i" class="glass-card pa-6 hover-lift" :style="`--c:${s.color}`">
            <div class="d-flex align-center justify-space-between mb-4">
              <v-icon large :color="s.color">{{ s.icon }}</v-icon>
              <span class="text-h3 font-weight-black" :style="`color:${s.color}`">{{ s.val }}</span>
            </div>
            <div class="text-h6 white--text mb-2">{{ s.label }}</div>
            <div class="progress-bg mb-4"><div class="progress-fill" :style="`width:${s.pct}%; background:${s.color}`"></div></div>
            <p class="caption grey--text mb-0">{{ s.desc }}</p>
          </div>
        </div>
      </div>

      <div class="py-12">
        <h2 class="section-title text-center mb-12">NUESTROS PRINCIPIOS</h2>
        <div class="principles-layout gap-8">
          <div class="grid-2-col gap-6">
            <div v-for="(p, i) in principles" :key="i" 
                 class="glass-card pa-6 hover-glow relative overflow-hidden"
                 :class="{active: activeP===i}" @mouseenter="activeP=i">
              <div class="d-flex align-center mb-4">
                <div class="icon-ring mr-4">
                  <v-icon large color="white">{{ p.icon }}</v-icon>
                </div>
                <h4 class="text-h6 font-weight-bold white--text">{{ p.title }}</h4>
              </div>
              <p class="grey--text text--lighten-2 mb-0">{{ p.desc }}</p>
              <div class="absolute-num">{{ i + 1 }}</div>
            </div>
          </div>
          <div class="glass-card pa-8 text-center d-flex flex-column justify-center sticky-top">
            <v-icon x-large color="cyan accent-3" class="mb-4">mdi-compass</v-icon>
            <h3 class="text-h5 font-weight-bold white--text mb-4">Nuestro Compromiso</h3>
            <p class="grey--text">Guía operativa para garantizar excelencia en Nicaragua.</p>
            <div class="d-flex justify-center gap-8 mt-auto">
              <div><div class="text-h4 cyan--text font-weight-black">6</div><div class="caption">Principios</div></div>
              <div><div class="text-h4 cyan--text font-weight-black">24/7</div><div class="caption">Aplicación</div></div>
            </div>
          </div>
        </div>
      </div>

      <div class="py-12 text-center">
        <h2 class="section-title mb-2">EL EQUIPO .NI</h2>
        <p class="code-font cyan--text mb-12">Expertos comprometidos con el desarrollo digital</p>
        <div class="grid-auto-fit gap-8">
          <div v-for="(m, i) in team" :key="i" class="glass-card pa-8 hover-lift group">
            <div class="avatar-wrap mx-auto mb-6 relative">
              <div class="avatar-circle glass-bg"><v-icon x-large color="white">mdi-account-network</v-icon></div>
              <div class="avatar-pulse"></div>
            </div>
            <h3 class="text-h5 white--text font-weight-bold mb-2">{{ m.role }}</h3>
            <p class="grey--text text--lighten-1 mb-4">{{ m.desc }}</p>
            <div class="d-flex justify-center flex-wrap gap-2">
              <span v-for="s in m.skills" :key="s" class="chip small">{{ s }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="glass-card pa-12 text-center my-12 cyan-gradient-border">
        <h3 class="text-h4 font-weight-bold white--text mb-4">¿LISTO PARA UNIRTE?</h3>
        <p class="grey--text text--lighten-1 mb-8 text-h6">Únete a la red digital de Nicaragua hoy mismo.</p>
        <div class="d-flex justify-center gap-4 flex-wrap">
         
        </div>
      </div>

      <div class="black rounded-lg overflow-hidden border-cyan">
        <div class="grey darken-4 pa-3 d-flex align-center gap-2 border-b-cyan">
          <div class="dot red"></div><div class="dot yellow"></div><div class="dot green"></div>
          <span class="code-font caption grey--text ml-2">system@nic.ni:/about</span>
        </div>
        <div class="pa-6 code-font green--text text--accent-3" style="min-height:200px">
          <div v-for="(l, i) in logs" :key="i" class="mb-2">
            <span class="cyan--text mr-2">nic@root:~$</span>{{ l.cmd }}<br>
            <span class="white--text ml-4" v-html="l.out"></span>
          </div>
          <div class="d-flex align-center mt-2">
            <span class="cyan--text mr-2">nic@root:~$</span>
            <input v-model="cmd" @keyup.enter="runCmd" class="transparent white--text border-none outline-none flex-grow-1 code-font" placeholder="Try: history, team, clear" autofocus>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
import vueHeadful from "vue-headful";

export default {
  name: "QuienesSomos",
  components: { vueHeadful },
  data: () => ({
    title: "NOSOTROS | NIC .NI",
    activeT: null, activeP: null, cmd: "",
    titles: ["Fundación NIC.NI", "Expansión", "Autonomía", "Gestión"],
    tags: [
      ["UNI", "Acuerdo 810"], ["Crecimiento", "Servicios"], 
      ["Sostenibilidad", "IPv4"], ["Redes", "Desarrollo"]
    ],
    historias: [
      {text: 'Creada mediante Acuerdo No. 810 (Oct 2013), adscrita a la UNI.'},
      {text: 'Org. sin fines de lucro dedicada a promover Internet en el país.'},
      {text: 'Autofinanciada sin presupuesto estatal, gestión propia de dominios.'},
      {text: 'Administración racional de reservas IPv4 y desarrollo.'}
    ],
    mv: [
      {icon: 'mdi-target', title: 'MISIÓN', text: 'Gestionar el espacio digital .ni y fortalecer la identidad nacional.'},
      {icon: 'mdi-eye', title: 'VISIÓN', text: 'Ser referente tecnológico impulsando la transformación digital.'}
    ],
    stats: [
      {val: "10K+", label: "Dominios", icon: "mdi-domain", color: "#00BCD4", pct: 85, desc: "Activos gestionados"},
      {val: "100%", label: "Uptime", icon: "mdi-server", color: "#E040FB", pct: 100, desc: "Monitoreo 24/7"},
      {val: "2013", label: "Desde", icon: "mdi-calendar", color: "#FF5252", pct: 100, desc: "Servicio continuo"},
      {val: "0", label: "Incidentes", icon: "mdi-shield-check", color: "#69F0AE", pct: 100, desc: "Seguridad total"}
    ],
    principles: [
      {icon: "mdi-shield-lock", title: "Seguridad", desc: "Protección de infraestructura"},
      {icon: "mdi-transit-connection", title: "Conectividad", desc: "Enlaces estables"},
      {icon: "mdi-chart-line", title: "Innovación", desc: "Vanguardia tecnológica"},
      {icon: "mdi-handshake", title: "Transparencia", desc: "Operación auditable"},
      {icon: "mdi-account-group", title: "Servicio", desc: "Soporte especializado"},
      {icon: "mdi-earth", title: "Nacionalidad", desc: "Identidad nicaragüense"}
    ],
    team: [
      {role: "Ingeniería de Redes", desc: "Infraestructura y protocolos", skills: ["Redes", "DNS", "IPv4"]},
      {role: "Soporte Técnico", desc: "Asistencia usuarios 24/7", skills: ["Soporte", "Cliente", "Resolución"]},
      {role: "Desarrollo", desc: "Plataformas digitales", skills: ["Software", "Web", "API"]}
    ],
    logs: [{cmd: "status", out: "System: <span class='green--text'>Operational</span>"}]
  }),
  methods: {
    nodeStyle: (i) => ({
      left: `${50 + Math.cos(i)*40}%`, top: `${50 + Math.sin(i)*40}%`,
      animationDelay: `${i*0.5}s`
    }),
    runCmd() {
      const c = this.cmd.trim().toLowerCase();
      let o = "Command not found.";
      if(c === 'history') o = "2013: Fundación<br>2015: Autonomía<br>Presente: Liderazgo";
      if(c === 'team') o = "Especialistas en redes y desarrollo.";
      if(c === 'clear') { this.logs = []; this.cmd = ""; return; }
      this.logs.push({cmd: this.cmd, out: o});
      this.cmd = "";
    }
  }
};
</script>

<style scoped>
:root { --primary: #00BCD4; --dark: #0a0f2b; }
.about-section { background: linear-gradient(135deg, #0a0f2b, #1a1a2e); min-height: 100vh; overflow-x: hidden; color: #fff; font-family: 'Roboto', sans-serif; }
.code-font { font-family: 'Courier New', monospace; }
.relative { position: relative; z-index: 2; }
.absolute-num { position: absolute; top: 10px; right: 15px; font-weight: 900; opacity: 0.1; font-size: 2rem; }

/* Binary Scrolling Animation */
.binary-scroll-container { width: 100%; overflow: hidden; white-space: nowrap; }
.binary-scrolling-text { display: inline-block; animation: scroll-left 30s linear infinite; }
@keyframes scroll-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

/* Background & Effects */
.bg-layer { position: fixed; inset: 0; z-index: 0; pointer-events: none; }
.net-grid { width: 100%; height: 100%; background: linear-gradient(90deg, rgba(0,188,212,0.05) 1px, transparent 1px), linear-gradient(0deg, rgba(0,188,212,0.05) 1px, transparent 1px); background-size: 50px 50px; }
.orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.15; width: 300px; height: 300px; }
.orb-1 { background: var(--primary); top: 10%; right: -10%; }
.orb-2 { background: #E040FB; bottom: 10%; left: -10%; }
.node { position: absolute; width: 6px; height: 6px; background: rgba(0,188,212,0.3); border-radius: 50%; animation: float 15s infinite linear; }
@keyframes float { 0% { transform: translateY(100vh); opacity:0;} 50% {opacity:1;} 100% {transform: translateY(-20vh); opacity:0;} }

/* Typography */
.nic-gradient { background: linear-gradient(45deg, #00BCD4, #3D5AFE); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.scan-line { height: 2px; width: 200px; background: linear-gradient(90deg, transparent, #00BCD4, transparent); animation: scan 3s infinite; }
@keyframes scan { 0% {transform: translateX(-100%);} 100% {transform: translateX(100%);} }

/* Timeline */
.timeline-line { position: absolute; left: 50%; top:0; bottom:0; width: 2px; background: rgba(255,255,255,0.1); transform: translateX(-50%); }
.timeline-content { width: 45%; transition: 0.3s; opacity: 0.7; transform: scale(0.95); }
.timeline-content.active { opacity: 1; transform: scale(1); }
.marker-dot { width: 16px; height: 16px; background: #0a0f2b; border: 2px solid var(--primary); border-radius: 50%; margin: 0 20px; position: relative; z-index: 2; transition: 0.3s; }
.marker-dot.active { background: var(--primary); box-shadow: 0 0 15px var(--primary); transform: scale(1.3); }

/* Cards & Layouts */
.glass-card { background: rgba(255,255,255,0.03); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; transition: 0.3s; }
.glass-bg { background: rgba(255,255,255,0.1); }
.hover-lift:hover { transform: translateY(-5px); border-color: var(--primary); box-shadow: 0 10px 30px rgba(0,0,0,0.3); }
.grid-auto-fit { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
.grid-2-col { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); }
.principles-layout { display: grid; grid-template-columns: 1fr 300px; }
.gap-2 { gap: 8px; } .gap-4 { gap: 16px; } .gap-6 { gap: 24px; } .gap-8 { gap: 32px; }

/* Components */
.chip { background: rgba(0,188,212,0.15); border: 1px solid rgba(0,188,212,0.3); padding: 4px 12px; border-radius: 12px; font-size: 0.8rem; color: #00BCD4; }
.chip.small { padding: 2px 10px; font-size: 0.75rem; }
.progress-bg { background: rgba(255,255,255,0.1); height: 6px; border-radius: 3px; overflow: hidden; }
.progress-fill { height: 100%; border-radius: 3px; }
.glow-bar { position: absolute; bottom:0; left:0; width:100%; height:3px; background: var(--primary); transform: scaleX(0); transition: 0.3s; }
.hover-lift:hover .glow-bar { transform: scaleX(1); }

/* Console */
.border-cyan { border: 1px solid rgba(0,188,212,0.3); }
.border-b-cyan { border-bottom: 1px solid rgba(0,188,212,0.3); }
.dot { width: 10px; height: 10px; border-radius: 50%; }
.dot.red { background: #ff5f56; } .dot.yellow { background: #ffbd2e; } .dot.green { background: #27c93f; }

/* Mobile */
@media (max-width: 960px) {
  .timeline-line { left: 20px; }
  .timeline-item { flex-direction: row !important; text-align: left !important; }
  .timeline-content { width: calc(100% - 60px); margin-left: auto; }
  .timeline-marker { position: absolute; left: 12px; }
  .principles-layout { grid-template-columns: 1fr; }
  .sticky-top { position: relative; top: 0; order: -1; }
}
</style>